<script setup lang="ts">
import {computed, nextTick, ref, watch} from "vue";
import {CircleAlert} from "@lucide/vue";
import {storeToRefs} from "pinia";
import {useAiAssistantStore} from "@/stores/aiAssistant.store";
import AssistantMessage from "@/ai-assistant/components/AssistantMessage.vue";
import {MAX_HISTORY_TURNS} from "@/ai-assistant/constants";
import {carriedFrom} from "@/ai-assistant/lib/transcript";
import {Proposition} from "@/ai-assistant/interfaces";

const {messages, error} = storeToRefs(useAiAssistantStore());

/**
 * Where the assistant's memory of this conversation starts.
 *
 * Shown rather than left to be discovered: a pharmacist who believes the
 * assistant still has the beginning of the exchange in mind will read its
 * answers as though it did, and the interface would be the thing that misled
 * them.
 */
const carried = computed<string | null>(() => carriedFrom(messages.value, MAX_HISTORY_TURNS));

const emits = defineEmits<{
    (event: 'adjust', proposition: Proposition): void;
}>();

const scroller = ref<HTMLElement | null>(null);

/**
 * Distance from the bottom under which the reader is taken to be following.
 */
const AT_THE_BOTTOM = 40;

/**
 * Keep the bottom of the conversation in view, once the DOM has caught up.
 *
 * Only for a reader who was already there. An answer is written at reading
 * pace over several seconds, and scrolling back up to compare it with the
 * previous one would otherwise be undone thirty times a second — the panel
 * would hold them at the bottom for as long as it takes to write.
 *
 * Measured before the DOM grows, which is where a watcher runs.
 */
const follow = async () => {
    const element = scroller.value;
    const following = !element
        || element.scrollHeight - element.scrollTop - element.clientHeight < AT_THE_BOTTOM;

    await nextTick();

    if (element && following) {
        element.scrollTop = element.scrollHeight;
    }
};

/**
 * Follow the answer as it is written, and the scenarios as they arrive.
 *
 * Watching the concatenated length rather than the array keeps the view at the
 * bottom while a message grows, which is when it matters most. The scenarios
 * have to count too: they land after the text is finished, they are tall, and
 * on their own they would render entirely below the fold with nothing saying
 * they are there. The bubbles report their own growth besides: an answer is
 * shown at reading pace, later than its text arrives.
 */
watch(
    () => messages.value.reduce(
        (total, message) => total
            + message.content.length
            + (message.propositions?.length ?? 0)
            + (message.propositionsLoading ? 1 : 0)
            + (message.propositionsError ? 1 : 0),
        messages.value.length
    ),
    follow
);
</script>

<template>
  <div ref="scroller" class="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
    <!--
      min-h-full and not h-full: centred content in a scroll container
      overflows both ways when the panel is short, and the part above the top
      edge can never be reached. This keeps the centring while there is room
      and lets the box grow past it when there is not.
    -->
    <div v-if="!messages.length" class="flex min-h-full flex-col items-center justify-center text-center">
      <p class="text-sm font-semibold text-primary">
        Posez votre question sur le planning de votre équipe
      </p>
      <p class="mt-1 text-xs leading-relaxed text-gray-dark/70">
        L'assistant vous propose, vous décidez.
      </p>
    </div>

    <template v-for="message in messages" :key="message.id">
      <p
        v-if="message.id === carried"
        class="flex items-center gap-2 py-0.5 text-[10px] uppercase tracking-wide text-gray-dark/45"
      >
        <span class="h-px flex-1 bg-gray-light" />
        L'assistant ne voit plus les échanges au-dessus
        <span class="h-px flex-1 bg-gray-light" />
      </p>

      <AssistantMessage
        :message="message"
        @adjust="emits('adjust', $event)"
        @grow="follow"
      />
    </template>

    <div
      v-if="error"
      class="flex items-start gap-1.5 rounded-lg bg-orange/20 px-2.5 py-2 text-[11px] leading-snug text-gray-dark"
    >
      <CircleAlert class="mt-px size-3.5 shrink-0 text-orange" />
      {{ error }}
    </div>
  </div>
</template>
